import { BarChart, type ChartReferenceLine } from './BarChart.js';
import type { ChartTone } from './chart-colors.js';

export interface HistogramBin {
  key: string;
  /** The bin's axis label (shown every `labelEvery`-th bin) and tooltip heading. */
  label: string;
  count: number;
  tone: ChartTone;
}

interface HistogramProps {
  bins: readonly HistogramBin[];
  /** Accessible name, e.g. "Trade returns". */
  title: string;
  height?: number;
  labelEvery?: number;
  /** Dashed markers on a bin (its `label`), e.g. the average loss and the average profit. */
  referenceLines?: readonly ChartReferenceLine[];
}

/** The screen-reader summary: how many items fell into how many non-empty bins. */
export function histogramSummary(bins: readonly HistogramBin[]): string {
  const total = bins.reduce((sum, bin) => sum + bin.count, 0);
  return `${total} in ${bins.filter((bin) => bin.count > 0).length} bins`;
}

/** Counts per fixed bin (e.g. trade returns in 1 %-point bins), each bar in its own tone. */
export function Histogram({ bins, title, height = 180, labelEvery = 5, referenceLines }: HistogramProps) {
  return (
    <BarChart
      data={bins.map((bin) => ({ label: bin.label, count: bin.count, tone: bin.tone }))}
      xKey="label"
      series={[{ key: 'count', label: 'Count' }]}
      barTone={(row) => row.tone}
      title={title}
      summary={histogramSummary(bins)}
      height={height}
      categoryInterval={labelEvery}
      referenceLines={referenceLines}
    />
  );
}
